/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// "Poll votes" panel (Figma 3466:10306) — the destination for the poll
// card's "View votes" affordance. Read-only modal listing the per-option
// breakdown: option label + vote count + the resolved voters (avatar +
// display name). Anonymous polls show a notice and counts only — never a
// name or avatar (privacy). Presentation-only: the HOST resolves
// `votersByOption` (account ids → avatar + name) exactly as it does for the
// in-card avatar stack, so the panel and the card always agree.
//
// className-first per the packages/ui styling rule.

import type React from "react";
import { Image, ScrollView, Text, View } from "react-native";
import { Modal } from "../components/Modal";
import type { PollViewContent, PollVoterAvatar } from "./PollView";

export interface PollVotesPanelProps {
	readonly visible: boolean;
	readonly poll: PollViewContent;
	/** `optionIndex -> voter count` (same projection the card uses). */
	readonly counts: Record<number, number>;
	/** Total distinct voters. */
	readonly total: number;
	/** Resolved voters per option (avatar + name). Empty when anonymous. */
	readonly votersByOption?: Record<number, ReadonlyArray<PollVoterAvatar>>;
	readonly onClose: () => void;
}

export const PollVotesPanel: React.FC<PollVotesPanelProps> = ({
	visible,
	poll,
	counts,
	total,
	votersByOption,
	onClose,
}) => {
	const anonymous = poll.anonymous === true;
	return (
		<Modal visible={visible} onClose={onClose} accessibilityLabel="Poll votes">
			<View className="w-[340px] max-w-full max-h-[540px] gap-2">
				{/* Header */}
				<View className="flex-row items-center justify-between">
					<Text className="text-[17px] font-bold text-light-text-primary dark:text-[#F5F6F8]">
						Poll votes
					</Text>
					<Text className="text-xs text-light-text-secondary dark:text-white/70">
						{total === 1 ? "1 vote" : `${total} votes`}
					</Text>
				</View>
				<Text
					className="text-[15px] font-medium text-light-text-primary dark:text-[#F5F6F8]"
					numberOfLines={4}
				>
					{poll.question}
				</Text>
				{anonymous ? (
					<Text className="text-xs italic text-light-text-tertiary dark:text-white/40">
						This poll is anonymous — individual votes are hidden.
					</Text>
				) : null}

				<ScrollView className="grow-0" contentContainerClassName="pb-2">
					{poll.options.map((option, index) => {
						const optionVotes = counts[index] ?? 0;
						const voters = anonymous ? [] : votersByOption?.[index] ?? [];
						return (
							<View
								key={`${poll.pollTarget}:votes:${index}`}
								className="border-t border-light-surface-tertiary py-2 dark:border-[#222429]"
							>
								<View className="mb-1 flex-row items-center gap-2">
									<Text
										className="flex-1 text-sm font-semibold text-light-text-primary dark:text-[#F5F6F8]"
										numberOfLines={2}
									>
										{option}
									</Text>
									<Text className="text-[13px] font-semibold text-light-text-secondary dark:text-white/70">
										{optionVotes}
									</Text>
								</View>
								{!anonymous && optionVotes === 0 ? (
									<Text className="pl-1 text-xs italic text-light-text-tertiary dark:text-white/40">
										No votes yet
									</Text>
								) : null}
								{!anonymous
									? voters.map((voter, i) => (
											<View
												key={`${index}:voter:${i}`}
												className="flex-row items-center gap-2 py-1"
											>
												{voter.avatarUrl !== undefined ? (
													<Image
														source={{ uri: voter.avatarUrl }}
														className="h-6 w-6 rounded-full"
														accessibilityIgnoresInvertColors
													/>
												) : (
													<View className="h-6 w-6 items-center justify-center rounded-full bg-[#B5BAC3]">
														<Text className="text-[9px] font-bold text-[#15171C]">
															{voter.initials.slice(0, 2)}
														</Text>
													</View>
												)}
												<Text
													className="flex-1 text-[13px] text-light-text-primary dark:text-white/90"
													numberOfLines={1}
												>
													{voter.name !== undefined && voter.name.length > 0
														? voter.name
														: "Member"}
												</Text>
											</View>
										))
									: null}
							</View>
						);
					})}
				</ScrollView>
			</View>
		</Modal>
	);
};
